/**
 * Feature Flag Type Definitions
 * Central types and interfaces for the feature flag system
 */

/**
 * Teams responsible for feature flag maintenance and decisions
 */
export type ResponsibleTeam = 'sit' | 'depot' | 'integrations' | 'beta';

/**
 * Feature flag categories for organization in the management UI
 */
export enum FeatureFlagCategory {
    UI_FEATURES = 'UI Features',
    BETA = 'Beta',
    EXPERIMENTAL = 'Experimental',
    DEBUGGING = 'Debugging',
    MAINTENANCE = 'Maintenance', // charger / station service tools
    SECURITY = 'Security', // certificate, access, identity
    ADMIN = 'Admin', // org-level or sysadmin visibility tools
}

/**
 * Risk level for feature flag categories
 */
export type FeatureFlagRiskLevel = 'Low' | 'Medium' | 'High' | 'Very High';

/**
 * Metadata about each feature flag category
 */
export interface FeatureFlagCategoryInfo {
    /** Category identifier */
    category: FeatureFlagCategory;
    /** Short description of the category */
    description: string;
    /** Detailed explanation of what belongs in this category */
    details: string;
    /** Example use cases */
    examples: string[];
    /** Typical intended audience */
    audience: string;
    /** Risk level associated with this category */
    risk: FeatureFlagRiskLevel;
}

/**
 * Complete metadata for all feature flag categories
 */
export const FEATURE_FLAG_CATEGORY_INFO: Record<FeatureFlagCategory, FeatureFlagCategoryInfo> = {
    [FeatureFlagCategory.UI_FEATURES]: {
        category: FeatureFlagCategory.UI_FEATURES,
        description: 'User Interface Features',
        details:
            'Features that directly affect the visible user interface or interaction flow for end users or customers. They typically include layout changes, new views, or UX improvements that are ready (or close to ready) for general use.',
        examples: ['Dark mode toggle', 'New dashboard layout', 'Navigation or menu visibility options'],
        audience: 'End users, sales demos, production customers',
        risk: 'Low',
    },
    [FeatureFlagCategory.BETA]: {
        category: FeatureFlagCategory.BETA,
        description: 'Controlled Public Release',
        details:
            'Flags used to gradually roll out stable features to limited user groups. These features are production-ready but still gathering real-world validation or feedback before a full release.',
        examples: [
            'New reporting or analytics modules',
            'Station notes or attachments tiles',
            'Customer preview dashboards',
        ],
        audience: 'Selected customer accounts, partner pilots',
        risk: 'Medium',
    },
    [FeatureFlagCategory.EXPERIMENTAL]: {
        category: FeatureFlagCategory.EXPERIMENTAL,
        description: 'Early Internal Experiments',
        details:
            'Used for features under development or active experimentation. These are not finalized and may change or be removed at any time. Intended for internal QA, R&D, or developer validation only.',
        examples: [
            'Station dynamics test configuration (FIFO vs. Democratic load sharing)',
            'OCPP version selector for protocol testing',
            'Internal prototype dashboards',
        ],
        audience: 'Developers, QA engineers, R&D testers',
        risk: 'High',
    },
    [FeatureFlagCategory.DEBUGGING]: {
        category: FeatureFlagCategory.DEBUGGING,
        description: 'Developer Diagnostics',
        details:
            'Flags that enable diagnostic overlays, raw data views, or troubleshooting tools for developers and testers. These should never be visible in production, as they may expose sensitive or low-level data.',
        examples: [
            'Visual feature flag indicators (borders, labels)',
            'EV or charger debug tabs',
            'Development consoles or trace overlays',
        ],
        audience: 'Developers, internal QA, testers',
        risk: 'High',
    },
    [FeatureFlagCategory.MAINTENANCE]: {
        category: FeatureFlagCategory.MAINTENANCE,
        description: 'Operational and Diagnostic Tools',
        details:
            'Flags for service engineers and field technicians that expose operational insights, diagnostic data, or maintenance utilities. These are safe for production systems but should only be visible to authorized support roles.',
        examples: [
            'Charger device model tab (live metrics view)',
            'Local maintenance dashboards',
            'Power distribution diagnostics',
        ],
        audience: 'Service engineers, depot technicians, support staff',
        risk: 'Medium',
    },
    [FeatureFlagCategory.SECURITY]: {
        category: FeatureFlagCategory.SECURITY,
        description: 'Access, Certificates, and Trust Management',
        details:
            'Reserved for features that impact authentication, encryption, or access control. They must be strictly limited to authorized personnel due to potential security implications.',
        examples: [
            'Certificate signing and management',
            'TLS / ISO15118 certificate visibility',
            'Role or identity configuration options',
        ],
        audience: 'System administrators, security engineers',
        risk: 'Very High',
    },
    [FeatureFlagCategory.ADMIN]: {
        category: FeatureFlagCategory.ADMIN,
        description: 'Administrative and Governance Features',
        details:
            'Features intended for system administrators, organization owners, or support leadership. These typically control data access, audit visibility, or tenant-level configuration tools.',
        examples: [
            'Viewing audit logs',
            'Legacy tenant access verification',
            'Organizational configuration dashboards',
        ],
        audience: 'Sysadmins, org admins, compliance officers',
        risk: 'High',
    },
};

/**
 * Complete feature flag definition with all metadata
 */
export interface FeatureFlagDefinition {
    /** Unique identifier for the flag (camelCase) */
    key: string;
    /** Human-readable name displayed in UI */
    label: string;
    /** Detailed description of what the flag does */
    description: string;
    /** Category for grouping in management UI */
    category: FeatureFlagCategory;
    /** Default state when flag is first created */
    defaultValue: boolean;
    /** Whether flag should only be available in development */
    developmentOnly: boolean;
    /** Team responsible for this flag */
    responsibleTeam?: ResponsibleTeam;
}
